import { Flex, Text } from '@tremor/react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import s from './GoogleAnalyticsConnector.module.scss';
import { useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { ConnectorFromStore } from '@/types/Connectors';
import { useOrganizationStore } from '@/stores/organizationStore';
import { assert } from '@/utils/asserts';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { IndexDB } from '@/utils/indexDB';
import { ConnectionSteps } from '@/components/ConnectionSteps/ConnectionSteps';
import { GOOGLE_ANALYTICS_MANUAL_CONNECTION_STEPS } from '@/utils/constants';
import { ConnectorTitle } from '../ConnectorTitle/ConnectorTitle';
import { ConnectorSubtitle } from '../ConnectorSubtitle/ConnectorSubtitle';
import { KeyInput } from './KeyInput';
import { OutlinedButton } from '../../Buttons/OutlinedButton/OutlinedButton';
import { BottomActions } from '../BottomActions/BottomActions';
import { ConnectorsService } from '@/services/connectorsService';
import { toastPromise } from '@/utils/toasts';

const manualGoogleAnalyticsSchema = z.object({
  gcpp_id: z.string().min(1, 'Please enter a valid GCPP ID'),
  bq_dataset_name: z.string().min(1, 'Please enter a valid BQ dataset name'),
});

type ManualGoogleAnalyticsSchema = z.infer<typeof manualGoogleAnalyticsSchema>;

export const GoogleAnalyticsEditManual = () => {
  const connector = useLoaderData() as ConnectorFromStore<{
    gcpp_id: string;
    bq_dataset_name: string;
  }>;
  const organization = useOrganizationStore((state) => state.selectedOrganization);
  assert(organization, 'Organization is not defined');
  const revalidator = useRevalidator();
  const initFormValues = useMemo(() => {
    if (connector) {
      return {
        gcpp_id: connector.config?.accounts?.[0]?.gcpp_id,
        bq_dataset_name: connector.config?.accounts?.[0]?.bq_dataset_name,
      };
    } else {
      return {
        gcpp_id: '',
        bq_dataset_name: '',
      };
    }
  }, [connector]);
  const workspaceId = useWorkspaceIdFromParams() as string;
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ManualGoogleAnalyticsSchema>({
    resolver: zodResolver(manualGoogleAnalyticsSchema),
    defaultValues: initFormValues,
  });

  const copyToClipboard = () => {
    navigator.clipboard.writeText('{nasz adres email}');
  };

  const addConnector = async (data: ManualGoogleAnalyticsSchema) => {
    const email = await IndexDB.getUserEmail();
    assert(typeof email !== 'undefined' && email !== null, 'User email is not defined');
    await ConnectorsService.setupConnector(
      workspaceId,
      'google-analytics',
      {
        isInitialSetup: true,
        type: 'google-analytics',
        name: 'Google Analytics',
        category: 'advertising',
        config: {
          accounts: [
            {
              gcpp_id: data.gcpp_id,
              bq_dataset_name: data.bq_dataset_name,
            },
          ],
        },
      },
      email,
      organization.id,
    );
  };

  const onSubmit = async (data: ManualGoogleAnalyticsSchema) => {
    setIsLoading(true);
    try {
      await toastPromise(
        addConnector(data),
        {
          success: {
            message: connector === null ? 'Added connector!' : 'Updated connector data!',
          },
          error: {
            message:
              connector === null
                ? 'Failed to add connector'
                : 'Failed to update connector data',
          },
        },
        { position: 'bottom-right', hideProgressBar: true, pauseOnHover: false },
      );
      revalidator.revalidate();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex flexDirection="col" alignItems="start">
      {connector === null ? (
        <ConnectionSteps
          style={{ marginBottom: '40px' }}
          steps={GOOGLE_ANALYTICS_MANUAL_CONNECTION_STEPS}
          currentStep={2}
        />
      ) : null}
      <ConnectorTitle>Google analytics / Raw Events Data</ConnectorTitle>
      <ConnectorSubtitle>
        Lorem ipsum dolor sit amet consectetur. Proin velit dui amet placerat:
      </ConnectorSubtitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <KeyInput
          title="Google Cloud Platform Project ID"
          label="Lorem ipsum dolor sit amet consectetur. Proin velit dui amet placerat. Rutrum
            tellus integer tempor egestas."
          id="gccp_id_input"
          error={!!errors.gcpp_id}
          errorMessage={errors.gcpp_id?.message}
          {...register('gcpp_id')}
        />
        <KeyInput
          title="BigQuery Dataset Name"
          label="Lorem ipsum dolor sit amet consectetur. Proin velit dui amet placerat. Rutrum
            tellus integer tempor egestas."
          id="gccp_id_input"
          error={!!errors.bq_dataset_name}
          errorMessage={errors.bq_dataset_name?.message}
          {...register('bq_dataset_name')}
        />
        <section className={s.manualCopyAccountWrapper}>
          <Text className={s.manualInstructionsText}>
            Please{' '}
            <span className={s.manualAddFollowingUserText}>
              add the following user - {`{nasz adres email}`}
            </span>{' '}
            - to your BigQuery dataset. See this video guide to learn how.
          </Text>
          <OutlinedButton onClick={copyToClipboard} className={s.manualCopyButton}>
            Copy this email to clipboard
          </OutlinedButton>
        </section>
        <BottomActions
          isCreating={connector === null}
          disabled={Object.keys(errors).length > 0}
          isLoading={isLoading}
        />
      </form>
    </Flex>
  );
};
